import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

async function getOrgId(): Promise<string> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");
  const { data, error } = await supabase
    .from("user_account")
    .select("org_id")
    .eq("external_auth_user", user.id)
    .single();
  if (error || !data) throw new Error("Failed to get organization");
  return data.org_id;
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface PmProgram {
  id: string;
  org_id: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PmProgramAsset {
  id: string;
  name: string;
  asset_type: string | null;
  status: string;
  make: string | null;
  model: string | null;
  serial_number: string | null;
  zone_id: string | null;
  zone_name: string | null;
  facility_id: string | null;
  facility_name: string | null;
}

// ── Hooks ────────────────────────────────────────────────────────────────────

export function usePmPrograms() {
  return useQuery({
    queryKey: ["pm_programs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pm_program")
        .select("*")
        .eq("is_active", true)
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data as PmProgram[];
    },
  });
}

export function usePmProgramsAdmin() {
  return useQuery({
    queryKey: ["pm_programs_admin"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pm_program")
        .select("*")
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data as PmProgram[];
    },
  });
}

export function useCreatePmProgram() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: { name: string; description?: string; sort_order?: number }) => {
      const org_id = await getOrgId();
      const { data, error } = await supabase
        .from("pm_program")
        .insert([{ ...input, org_id }])
        .select()
        .single();
      if (error) throw error;
      return data as PmProgram;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pm_programs"] });
      qc.invalidateQueries({ queryKey: ["pm_programs_admin"] });
      toast({ title: "PM Program created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

export function useUpdatePmProgram() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...input }: Partial<PmProgram> & { id: string }) => {
      const { data, error } = await supabase
        .from("pm_program")
        .update(input)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as PmProgram;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pm_programs"] });
      qc.invalidateQueries({ queryKey: ["pm_programs_admin"] });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

export function useDeletePmProgram() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pm_program").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pm_programs"] });
      qc.invalidateQueries({ queryKey: ["pm_programs_admin"] });
      toast({ title: "PM Program deleted" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

// ── Asset ↔ Program assignments ──────────────────────────────────────────────

/** Returns program IDs assigned to a specific asset */
export function useAssetPmProgramIds(assetId: string | null) {
  return useQuery({
    queryKey: ["asset_pm_programs", assetId],
    enabled: !!assetId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_pm_program")
        .select("pm_program_id")
        .eq("asset_id", assetId!);
      if (error) throw error;
      return (data ?? []).map((r) => r.pm_program_id as string);
    },
  });
}

/** Replace all PM program assignments for an asset in one call */
export function useSetAssetPmPrograms() {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ assetId, programIds }: { assetId: string; programIds: string[] }) => {
      const org_id = await getOrgId();

      // Delete existing
      const { error: delError } = await supabase
        .from("asset_pm_program")
        .delete()
        .eq("asset_id", assetId);
      if (delError) throw delError;

      if (programIds.length === 0) return;

      // Insert new
      const rows = programIds.map((pm_program_id) => ({ asset_id: assetId, pm_program_id, org_id }));
      const { error: insError } = await supabase.from("asset_pm_program").insert(rows);
      if (insError) throw insError;
    },
    onSuccess: (_data, { assetId }) => {
      qc.invalidateQueries({ queryKey: ["asset_pm_programs", assetId] });
      qc.invalidateQueries({ queryKey: ["pm_program_assets"] });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });
}

/** Returns all assets assigned to a PM program, with zone/facility info */
export function usePmProgramAssets(programId: string | null) {
  return useQuery({
    queryKey: ["pm_program_assets", programId],
    enabled: !!programId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("asset_pm_program")
        .select(`
          asset:asset_id (
            id,
            name,
            asset_type,
            status,
            make,
            model,
            serial_number,
            zone_id,
            facility_id,
            facility:facility_id ( name ),
            zone:zone_id ( name )
          )
        `)
        .eq("pm_program_id", programId!);
      if (error) throw error;

      return (data ?? []).map((r) => {
        const a = r.asset as any;
        return {
          id: a.id,
          name: a.name,
          asset_type: a.asset_type,
          status: a.status,
          make: a.make,
          model: a.model,
          serial_number: a.serial_number,
          zone_id: a.zone_id,
          zone_name: a.zone?.name ?? null,
          facility_id: a.facility_id,
          facility_name: a.facility?.name ?? null,
        } as PmProgramAsset;
      });
    },
  });
}
